import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConfigService } from '@neworg-net/amsuite-core/config';
import { requireHostUrl } from '../shared/required-host-url';
import { httpOptions } from '../constants/http-options.constant';
import {
  adaptWorkspaceDashboard,
  toTemplateCreateRequest,
  toTemplateUpdateRequest
} from './workspace-dashboard.adapter';
import {
  AvailableTemplateDto,
  CreateTemplateRequest,
  LegacyDashboard,
  UpdateTemplateRequest,
  WorkspaceDashboardDto
} from '../models';

/**
 * Template dashboard del servizio Workspace (creazione + gestione).
 *
 * - Creazione: GET /v1/dashboards/templates (template usabili) e
 *   POST /v1/dashboards/from-template (clona in dashboard utente).
 * - Gestione: /v1/admin/templates* (list/detail/create/update/delete per
 *   livello Tenant e System).
 *
 * Le risposte DashboardDto sono adattate alla shape legacy (`LegacyDashboard`)
 * cosi da poter riusare direttamente l'editor/Gridster di manage-dashboard.
 * Bearer + X-Party-Owner-Id sono iniettati dagli interceptor per ogni URL
 * sotto `baseUrlWorkspace`: nessun header impostato manualmente qui.
 */
@Injectable({ providedIn: 'root' })
export class WorkspaceTemplateService {
  private _http = inject(HttpClient);
  private readonly config = inject(ConfigService);

  private workspaceUrl = requireHostUrl(this.config, 'workspace') + '/v1/dashboards';
  private adminUrl = requireHostUrl(this.config, 'workspace') + '/v1/admin/templates';

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);
  constructor() {}

  // ---------- CREAZIONE (feature A) ----------

  /** Template utilizzabili in creazione (System U Tenant del PO). */
  getCreationTemplates(): Observable<AvailableTemplateDto[]> {
    const url = `${this.workspaceUrl}/templates?appIdQuery=${this.config.appId}`;
    return this._http.get<AvailableTemplateDto[]>(url, httpOptions).pipe(map(list => list ?? []));
  }

  /** Clona un template in una nuova dashboard utente. */
  createFromTemplate(templateId: string, name?: string): Observable<LegacyDashboard> {
    const url = `${this.workspaceUrl}/from-template`;
    const body = name != null ? { templateId, name } : { templateId };
    return this._http.post<WorkspaceDashboardDto>(url, body, httpOptions).pipe(map(adaptWorkspaceDashboard));
  }

  // ---------- GESTIONE TEMPLATE (feature C) ----------

  /** Template gestibili (admin: System+Tenant; supervisor/appmanager: solo Tenant). */
  getManageableTemplates(): Observable<AvailableTemplateDto[]> {
    const url = `${this.adminUrl}?appIdQuery=${this.config.appId}`;
    return this._http.get<AvailableTemplateDto[]>(url, httpOptions).pipe(map(list => list ?? []));
  }

  /** Dettaglio template per editing (adattato a shape legacy). */
  getTemplate(id: string): Observable<LegacyDashboard> {
    const url = `${this.adminUrl}/${id}`;
    return this._http.get<WorkspaceDashboardDto>(url, httpOptions).pipe(map(adaptWorkspaceDashboard));
  }

  // ----- Tenant -----

  createTenantTemplate(req: CreateTemplateRequest): Observable<LegacyDashboard> {
    const url = `${this.adminUrl}/tenant`;
    return this._http.post<WorkspaceDashboardDto>(url, req, httpOptions).pipe(map(adaptWorkspaceDashboard));
  }

  updateTenantTemplate(id: string, req: UpdateTemplateRequest): Observable<LegacyDashboard> {
    const url = `${this.adminUrl}/tenant/${id}`;
    return this._http.put<WorkspaceDashboardDto>(url, req, httpOptions).pipe(map(adaptWorkspaceDashboard));
  }

  deleteTenantTemplate(id: string): Observable<void> {
    const url = `${this.adminUrl}/tenant/${id}`;
    return this._http.delete<void>(url, httpOptions);
  }

  // ----- System -----

  createSystemTemplate(req: CreateTemplateRequest): Observable<LegacyDashboard> {
    const url = `${this.adminUrl}/system`;
    return this._http.post<WorkspaceDashboardDto>(url, req, httpOptions).pipe(map(adaptWorkspaceDashboard));
  }

  updateSystemTemplate(id: string, req: UpdateTemplateRequest): Observable<LegacyDashboard> {
    const url = `${this.adminUrl}/system/${id}`;
    return this._http.put<WorkspaceDashboardDto>(url, req, httpOptions).pipe(map(adaptWorkspaceDashboard));
  }

  deleteSystemTemplate(id: string): Observable<void> {
    const url = `${this.adminUrl}/system/${id}`;
    return this._http.delete<void>(url, httpOptions);
  }

  // ---------- Helper di costruzione body (riuso adapter) ----------

  /** Costruisce il body create da widget legacy correnti (es. mapDashboardModel(...).widgets). */
  buildCreateRequest(appId: string, name: string, legacyWidgets: any[] = [], isDefault = false): CreateTemplateRequest {
    return toTemplateCreateRequest(appId, name, legacyWidgets, isDefault);
  }

  /** Costruisce il body update da widget legacy correnti; isDefault opzionale. */
  buildUpdateRequest(name: string, legacyWidgets: any[] = [], isDefault?: boolean): UpdateTemplateRequest {
    return toTemplateUpdateRequest(name, legacyWidgets, isDefault);
  }
}
